import { IToolManifest } from '../../../core/tools/interfaces/ITool';
import { ICommand } from '../../../core/commands/ICommand';
import { ColorTool } from '../../../core/tools/standard/ColorTool';
import { Node } from '../../../core/models/scene';
import { ChangeNodeColorCommand } from '../../../core/commands/shapes/ChangeNodeColorCommand';
import i18n from '../../../i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';

// Genogram-specific color tool
export class GenogramColorTool extends ColorTool {
  manifest: IToolManifest;
  
  private nodes: Node[] = [];
  
  constructor() {
    super();
    
    // Customize presets for genogram
    this.presetColors = {
      [i18n.t('genogram:colors.preset.default', { defaultValue: 'Default' }) as string]: '#f5f5f5',
      [i18n.t('genogram:colors.preset.italy', { defaultValue: 'Italy' }) as string]: '#CD853F',
      [i18n.t('genogram:colors.preset.uk', { defaultValue: 'UK' }) as string]: '#4682B4',
      [i18n.t('genogram:colors.preset.vietnam', { defaultValue: 'Vietnam' }) as string]: '#90EE90',
      [i18n.t('genogram:person.male', { defaultValue: 'Male' }) as string]: '#E3F2FD',
      [i18n.t('genogram:person.female', { defaultValue: 'Female' }) as string]: '#FCE4EC',
      [i18n.t('genogram:person.deceased', { defaultValue: 'Deceased' }) as string]: '#9E9E9E',
      [i18n.t('genogram:colors.preset.highlighted', { defaultValue: 'Highlighted' }) as string]: '#FFE082'
    };
    
    // Build manifest from base and add genogram-specific properties
    this.manifest = {
      ...this.getBaseManifest(),
      id: 'genogram-color',
      name: i18n.t('genogram:tools.nodeColor', { defaultValue: 'Node Color' }) as string,
      description: 'Change person node colors',
      supportedDiagramTypes: ['genogram'],
      supportedShapeTypes: ['person'],
    } as IToolManifest;
    bindGenogramToolName(this.manifest, 'tools.nodeColor', 'Node Color');
  }
  
  setNodes(nodes: Node[]): void {
    this.nodes = nodes;
  }
  
  getItemColor(itemId: string): string | undefined {
    const node = this.nodes.find(n => n.id === itemId);
    return node?.fillColor;
  }
  
  setItemColor(_itemId: string, _color: string | undefined): void {
    // This method should NOT be called in the proper architecture
    // State mutations should only happen through command execution
    console.warn('setItemColor called directly - this violates command pattern');
  }
  
  getSelectedItems(): string[] {
    if (!this.context) return [];
    
    const selectedNodeIds = Array.from(this.context.selection);
    
    // Filter to only person nodes
    return selectedNodeIds.filter(id => 
      this.nodes.find(n => n.id === id && n.type === 'person')
    );
  }
  
  /**
   * Override execute to return proper command
   */
  execute(params: { color: string }): ICommand | null {
    if (!params.color) return null;
    
    const selectedIds = this.getSelectedItems();
    if (selectedIds.length === 0) return null;
    
    // Return a proper command that will be executed by CommandManager
    return new ChangeNodeColorCommand(selectedIds, params.color);
  }
}
